/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import PropTypes from 'prop-types' // Npm: react.js library.
import _ from 'underscore' // Npm: underscore library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { toast } from 'react-hot-toast' // Npm: React toastify.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import QuickFileUpload from 'components/QuickFileUpload'
import Modal from 'components/Modal'
import TableSpinner from 'components/TableSpinner'
import KeywordDropDownOptions from 'components/KeywordDropDownOptions'
import KeywordUpsert from 'components/KeywordUpsert'
import KeywordDelete from 'components/KeywordDelete'


/*
 * GRAPHS
 */
import KeywordReadQuery from './__query__/index.keyword.read.query'
import KeywordUploadMutation from './__mutation__/index.keyword.upload.mutation'


/*
 * STYLES
 */
import {
  buttonStyle
} from './index.style'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {

  const _successFlags = React.useMemo(() => Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key), [])

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [searchQuery, setSearchQuery] = React.useState('')
  const [MutationKeywordUpload] = useMutation(KeywordUploadMutation)
  const { 'isOpen': isKeywordCreateOpen, 'onOpen': onKeywordCreateOpen, 'onClose': onKeywordCreateClose } = useDisclosure()
  const { 'isOpen': isKeywordUpdateOpen, 'onOpen': onKeywordUpdateOpen, 'onClose': onKeywordUpdateClose } = useDisclosure()
  const { 'isOpen': isKeywordDeleteOpen, 'onOpen': onKeywordDeleteOpen, 'onClose': onKeywordDeleteClose } = useDisclosure()
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)
  const _rowCountRef = React.useRef(0)
  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false
    setPaginationModel(j => {
      const nextPaginationModel = { ...j, 'page': 0 }

      _paginationModelRef.current = nextPaginationModel

      return nextPaginationModel
    })
  }, [])
  const _QueryKeywordReadVariables = React.useMemo(() => ({
    keywordDirectoryId: passOn?.keywordDirectoryId,
    keywordToSearch: searchQuery,
    skip: paginationModel?.pageSize * paginationModel?.page,
    take: paginationModel?.pageSize
  }), [paginationModel?.page, paginationModel?.pageSize, passOn?.keywordDirectoryId, searchQuery])
  const _QueryKeywordRead = useQuery(KeywordReadQuery, {
    variables: _QueryKeywordReadVariables,
    fetchPolicy: _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy,
  })
  const _onSearchInputChange = React.useRef(
    _.debounce((value) => {
      _ResetPagination()
      setSearchQuery(value)
    }, 800),
  ).current
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])
  const _HandleFilterModelChange = React.useCallback(__filterModel => {
    _ResetPagination()
    setFilterModel(__filterModel)
  }, [_ResetPagination])
  const _HandleColumnVisibilityModelChange = React.useCallback(newModel => {
    setColumnVisibilityModel(newModel)
  }, [])
  const _QueryKeywordReadRowCount = _.pluck(
    _QueryKeywordRead?.data?.KeywordRead,
    '_totalCount'
  )?.[0]
  const rowCount = React.useMemo(() => {
    if (undefined !== _QueryKeywordReadRowCount && null !== _QueryKeywordReadRowCount) _rowCountRef.current = _QueryKeywordReadRowCount

    return _rowCountRef.current
  }, [_QueryKeywordReadRowCount])
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  }) ?? false;

  // Extract search from filter model
  React.useEffect(() => {
    const keywordFilter = filterModel?.items?.find(
      (item) => item.field === "keyword",
    );
    const searchValue = keywordFilter?.value || "";
    _onSearchInputChange(searchValue);
  }, [_onSearchInputChange, filterModel]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.();
    };
  }, []);

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _QueryKeywordRead.refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [_QueryKeywordRead.refetch]);

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 210
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'keyword',
      'headerName': 'Keyword',
      'description': 'Keyword name.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 350
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Created date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 350
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Updated date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 350
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions for keyword.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 170,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => <KeywordDropDownOptions
        keywordId={row?.id}
        keywordDirectoryId={passOn?.keywordDirectoryId}
        onKeywordUpdate={onKeywordUpdateOpen}
        onKeywordDelete={onKeywordDeleteOpen}
      />
    }
  ], [passOn?.keywordDirectoryId, onKeywordUpdateOpen, onKeywordDeleteOpen])

  const rows = React.useMemo(() => _.compact(_QueryKeywordRead?.data?.KeywordRead?.map((r, index) => _successFlags.includes(r.status) ? ({
    'id': r?.id ?? `keyword-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'keyword': r?.keyword ?? '-',
    'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD'),
    'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD')
  }) : null)), [_QueryKeywordRead?.data?.KeywordRead, _successFlags, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className="keyword base">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="81vh"
          maxH="81vh"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex
            pb="0px"
            justify="space-between"
            gap="12px"
            flexWrap={{ base: "wrap", md: "nowrap" }}
          >
            <Tooltip label="Create New Keyword" fontSize="sm">
              <Button
                w="170px"
                leftIcon={<IoMdAdd />}
                onClick={onKeywordCreateOpen}
                style={buttonStyle}
              >
                Create Keyword
              </Button>
            </Tooltip>
            <Flex gap="12px">
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <QuickFileUpload
                onChange={(j) => {
                  // Const assignment.
                  const _keywords = Array.from(
                    { length: j?.rows?.length },
                    (__, i) => _.first(j?.rows[i]),
                  );

                  // Execute mutation.
                  return MutationKeywordUpload({
                    variables: {
                      keywordDirectoryId: passOn?.keywordDirectoryId,
                      keywords: _keywords,
                    },
                  }).then((jp) => {
                    // On Successful response from the mutation.
                    if (
                      "UPSERT_SUCCESSFUL" === jp?.data?.KeywordUpload?.status
                    ) {
                      // Toast success.
                      toast(
                        "Successfully uploaded the keywords in your keyword directory.",
                      );
                    } else {
                      // Toast failure.
                      toast(
                        jp?.data?.KeywordUpload?.message ??
                          "Failed to upload the keywords in your keyword directory.",
                      );
                    }
                  });
                }}
                isLoading={false}
              />
              <DownloadToExcel
                cellsData={
                  0 === _QueryKeywordRead?.data?.KeywordRead?.length ||
                  !_.every(
                    _.pluck(_QueryKeywordRead?.data?.KeywordRead, "status"),
                    (i) => _successFlags.includes(i),
                  )
                    ? []
                    : _QueryKeywordRead?.data?.KeywordRead.map(
                        (item, __index) => ({
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          CreatedAt: Moment(item?.createdAt).format(
                            "YYYY-MM-DD",
                          ),
                          UpdatedAt: Moment(item?.updatedAt).format(
                            "YYYY-MM-DD",
                          ),
                          Keyword: item.keyword ?? "-",
                        }),
                      )
                }
                headersData={["S.No.", "CreatedAt", "UpdatedAt", "Keyword"].map(
                  (i) => ({ key: i, label: i }),
                )}
              />
            </Flex>
          </Flex>
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={_HandleFilterModelChange}
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={_HandleColumnVisibilityModelChange}
            loading={_QueryKeywordRead?.loading}
          />
        </Flex>
        <Modal
          size="md"
          title="Keyword Create"
          isOpen={isKeywordCreateOpen}
          onClose={onKeywordCreateClose}
        >
          <KeywordUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size="md"
          title="Keyword Update"
          isOpen={isKeywordUpdateOpen}
          onClose={onKeywordUpdateClose}
        >
          <KeywordUpsert />
        </Modal>
        <Modal
          size="md"
          title="Keyword Delete"
          isOpen={isKeywordDeleteOpen}
          onClose={onKeywordDeleteClose}
        >
          <KeywordDelete />
        </Modal>
      </Flex>
    </>
  );
}



/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
